// Core authentication types shared across the application
// This file contains the fundamental types that both AuthService and AuthStore need

// Permission enum - aligned with backend, represents JWT groups
export enum Permission {
  ASSET_FINDER = 'ASSET_FINDER',
  TRAY_ASSEMBLER = 'TRAY_ASSEMBLER',
  TRAY_SCANNER = 'TRAY_SCANNER',
  OR_ASSIGN = 'OR_ASSIGN',
  SPD_RELEASE = 'SPD_RELEASE',
  IMPLEMENTATION_ENGINEER = 'IMPLEMENTATION_ENGINEER',
  CART_BUILDER = 'CART_BUILDER',
  ADMIN = 'ADMIN',
  OR_NURSE = 'OR_NURSE',
}

// User roles enum - aligned with backend
export enum UserRole {
  ADMIN = 'ADMIN',
  ASSET_FINDER = 'ASSET_FINDER',
  TRAY_ASSEMBLER = 'TRAY_ASSEMBLER',
  TRAY_SCANNER = 'TRAY_SCANNER',
  OR_ASSIGN = 'OR_ASSIGN',
  SPD_RELEASE = 'SPD_RELEASE',
  IMPLEMENTATION_ENGINEER = 'IMPLEMENTATION_ENGINEER',
  CART_BUILDER = 'CART_BUILDER',
  OR_NURSE = 'OR_NURSE',
}

export enum LogoutReason {
  MANUAL = 'manual',
  TIMEOUT = 'timeout',
  REMOTE = 'remote',
}

export interface LogoutOptions {
  reason?: LogoutReason;
  broadcast?: boolean;
}

// Core user interface (minimal data for security)
export interface User {
  id: string;
  username: string;
  permissions: Permission[];
  isAdmin: boolean;
  isActive: boolean;
  inactivityLimitMs: number | null;
  sessionKey: string | null;
}

// Extended user data (fetched when needed)
export interface UserProfile extends User {
  email: string;
  firstName: string;
  lastName: string;
  lastLogin?: Date;
  createdAt: Date;
  updatedAt: Date;
}

// Authentication state interface (most secure)
export interface AuthState {
  isAuthenticated: boolean;
  user: User | null; // Minimal user data only
  isLoading: boolean;
  error: string | null;
}

// Login response interface (secure)
export interface LoginResponse {
  user: User; // Minimal user data only
}

// Authentication context interface (secure)
export interface AuthContextType {
  // State
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;

  // Actions
  login: () => Promise<void>;
  logout: (options?: LogoutOptions) => Promise<void>;
  clearError: () => void;
  initialize: () => Promise<void>;

  // PKCE callback handling
  handlePKCECallback: (
    code: string,
    codeVerifier: string,
    redirectUri: string
  ) => Promise<void>;

  // User profile management (secure)
  getUserProfile: () => Promise<UserProfile | null>;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>;

  // Utilities
  hasPermission: (permission: Permission) => boolean;
  isAdmin: () => boolean;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
}

// Navigation item interface (for permission-based navigation)
export interface NavigationItem {
  label: string;
  path: string;
  icon?: string;
  permissions?: Permission[];
  adminOnly?: boolean;
  requiresAuth: boolean;
  children?: NavigationItem[];
}

// Backend API Integration Types

/**
 * Backend User Format (from ZITADEL)
 * roles and permissions are optional - will be removed from callback, use JWT groups instead
 */
export interface BackendUser {
  id?: string;
  userId?: string;
  email: string;
  firstName: string;
  lastName: string;
  roles?: string[];
  permissions?: string[];
  isActive?: boolean;
  lastActivity?: number;
}

/**
 * Logout Response
 */
export interface LogoutResponse {
  success: boolean;
  message: string;
}

// Note: BackendSessionResponse, TokenExchangeRequest, TokenExchangeResponse
// are defined in api.client.ts (used by session.service). Duplicates removed - JWT migration.
